import { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export function PdfViewer({ fileUrl, initialPage = 1, onPageChange, testIdPrefix = "pdf-viewer" }) {
  const canvasRef = useRef(null);
  const pdfRef = useRef(null);
  const [page, setPage] = useState(initialPage);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    pdfjsLib.getDocument({ url: fileUrl }).promise.then((pdf) => {
      if (cancelled) return;
      pdfRef.current = pdf;
      setNumPages(pdf.numPages);
      setPage(Math.min(Math.max(1, initialPage), pdf.numPages));
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { setError("This document could not be loaded."); setLoading(false); }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl]);

  const renderPage = useCallback(async (pageNum) => {
    const pdf = pdfRef.current;
    if (!pdf || !canvasRef.current || pageNum < 1) return;
    const pg = await pdf.getPage(pageNum);
    const viewport = pg.getViewport({ scale });
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await pg.render({ canvasContext: ctx, viewport }).promise;
  }, [scale]);

  useEffect(() => {
    if (pdfRef.current && page) {
      renderPage(page);
      onPageChange?.(page, numPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, scale, numPages]);

  const search = async () => {
    const term = searchTerm.trim().toLowerCase();
    if (!term || !pdfRef.current) return;
    setSearching(true);
    setSearchResult(null);
    const pdf = pdfRef.current;
    for (let i = 1; i <= pdf.numPages; i++) {
      const pg = await pdf.getPage(i);
      const content = await pg.getTextContent();
      const text = content.items.map((it) => it.str).join(" ").toLowerCase();
      if (text.includes(term)) {
        setSearchResult({ page: i });
        setPage(i);
        setSearching(false);
        return;
      }
    }
    setSearchResult({ page: null });
    setSearching(false);
  };

  if (error) return <div data-testid={`${testIdPrefix}-error`} className="text-center py-20 opacity-70">{error}</div>;

  return (
    <div className="flex flex-col items-center h-full w-full" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 py-3 px-4 border-b border-white/10 w-full justify-center flex-wrap">
        <button data-testid={`${testIdPrefix}-prev-page`} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="text-sm opacity-60 hover:opacity-100 disabled:opacity-20">← Prev</button>
        <input
          data-testid={`${testIdPrefix}-page-input`}
          type="number"
          value={page}
          min={1}
          max={numPages || 1}
          onChange={(e) => setPage(Math.min(Math.max(1, Number(e.target.value) || 1), numPages || 1))}
          className="w-14 bg-transparent border border-white/20 text-center text-sm py-1"
        />
        <span className="text-xs opacity-50">/ {numPages || "…"}</span>
        <button data-testid={`${testIdPrefix}-next-page`} disabled={page >= numPages} onClick={() => setPage((p) => Math.min(numPages, p + 1))} className="text-sm opacity-60 hover:opacity-100 disabled:opacity-20">Next →</button>
        <button data-testid={`${testIdPrefix}-zoom-out`} onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(1)))} className="text-sm opacity-60 hover:opacity-100 px-1">−</button>
        <button data-testid={`${testIdPrefix}-zoom-in`} onClick={() => setScale((s) => Math.min(2.4, +(s + 0.2).toFixed(1)))} className="text-sm opacity-60 hover:opacity-100 px-1">+</button>
        <input
          data-testid={`${testIdPrefix}-search-input`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Search in book…"
          className="bg-transparent border border-white/20 text-sm px-2 py-1 w-32"
        />
        <button data-testid={`${testIdPrefix}-search-button`} onClick={search} disabled={searching} className="text-sm opacity-60 hover:opacity-100">
          {searching ? "…" : "Find"}
        </button>
        {searchResult && (
          <span data-testid={`${testIdPrefix}-search-result`} className="text-xs opacity-50">
            {searchResult.page ? `Found on page ${searchResult.page}` : "Not found"}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-auto w-full flex justify-center py-6">
        {loading ? <p className="opacity-50 text-sm">Loading…</p> : <canvas ref={canvasRef} data-testid={`${testIdPrefix}-canvas`} className="shadow-lg max-w-full" />}
      </div>
    </div>
  );
}
